import queryString from 'query-string';
import axios, { CancelToken } from 'axios';
import hash from 'object-hash';
import { each, keys, isFunction, filter } from 'lodash';
import PriorityQueue from './PriorityQueue';

export class RequestHandler {
  constructor(maxActive, shouldLog = false) {
    this.priorityQueue = new PriorityQueue(RequestHandler.comparatorSmallestFirst, RequestHandler.keyGenerator);

    this.active = {};
    this.numActive = 0;
    this.maxActive = maxActive;
    this.shouldLog = shouldLog;

    this.getActiveRequestKeysByPredicate = this.getActiveRequestKeysByPredicate.bind(this);
  }

  static comparatorSmallestFirst = (request) => request.priority;

  static keyGenerator({ requestConfig }) {
    const { url, params, data } = requestConfig;
    const flatParams = params ? queryString.stringify(params) : '';
    const flatBody = data ? hash(data) : '';

    return `${url}|${flatParams}|${flatBody}`;
  }

  static mapPredicateToQueue(predicate) {
    return ({ requestConfig }) => predicate(requestConfig);
  }

  static triggerPredicate(active, predicate) {
    return (key) => predicate(active[key].requestConfig);
  }

  logRequests() {
    const queued = this.priorityQueue.getKeys();
    const message = `[RequestHandler] ${keys(this.active).length} active; ${queued.length} queued`;

    console.group ? console.group(message) : console.log(message);

    keys(this.active, (key) => console.log(` - [ACTIVE] ${key}`));
    each(queued, (key) => console.log(` - [QUEUED] ${key}`));
    console.groupEnd && console.groupEnd();
  }

  process(requestConfig, priority, { onPending, onSuccess, onFailure, onCancel }) {
    if (!requestConfig.url || !requestConfig.method) {
      console.error('RequestHandler process - received request config without method or url');
      return;
    }

    if (this.isAvailableSlot()) {
      this.send(requestConfig, onPending, onSuccess, onFailure, onCancel);
    } else {
      this.enqueueRequest(priority, requestConfig, onPending, onSuccess, onFailure, onCancel);
    }

    this.shouldLog && this.logRequests();
  }

  send(requestConfig, onPending, onSuccess, onFailure, onCancel) {
    const source = CancelToken.source();
    const key = RequestHandler.keyGenerator({ requestConfig });
    const requestConfigWithToken = { ...requestConfig, cancelToken: source.token };

    axios(requestConfigWithToken).then(this.onFinish(key, onSuccess), this.onFinish(key, this.cancelOrFailure(onFailure, onCancel)));

    this.onSend(key, source, requestConfigWithToken, onPending);
  }

  cancelOrFailure(onFailure, onCancel) {
    return (result) => (axios.isCancel(result) ? onCancel(result) : onFailure(result));
  }

  onFinish(key, cb) {
    return (result) => {
      this.removeActiveRequest(key);
      isFunction(cb) && cb(result);
      this.processNextRequest();
    };
  }

  processNextRequest() {
    const nextQueuedItem = this.priorityQueue.dequeue();
    if (nextQueuedItem) {
      const { requestConfig, priority, onPending, onSuccess, onFailure, onCancel } = nextQueuedItem;

      this.process(requestConfig, priority, { onPending, onSuccess, onFailure, onCancel });
    }
  }

  cancelActiveRequest(key) {
    this.active[key].source.cancel(`[RequestHandler] request with key "${key}" was canceled`);
    this.removeActiveRequest(key);
  }

  removeActiveRequest(key) {
    delete this.active[key];
    this.numActive--;
  }

  removeQueuedRequestBy(predicate) {
    this.priorityQueue.removeBy(predicate);
  }

  removeQueuedRequest(key) {
    this.priorityQueue.dequeue(key);
  }

  cancel(requestConfigOrPredicate) {
    const isPredicate = isFunction(requestConfigOrPredicate);

    if (isPredicate) {
      this.cancelByPredicate(requestConfigOrPredicate);
    } else {
      this.cancelByKey(RequestHandler.keyGenerator({ requestConfig: requestConfigOrPredicate }));
    }
  }

  cancelByPredicate(predicate) {
    const keys = this.getActiveRequestKeysByPredicate(predicate);
    each(keys, (key) => this.cancelByKey(key));
    this.removeQueuedRequestBy(RequestHandler.mapPredicateToQueue(predicate));
  }

  cancelByKey(key) {
    if (this.active[key]) {
      this.cancelActiveRequest(key);
    } else {
      this.removeQueuedRequest(key);
    }
  }

  getActiveRequestKeysByPredicate(predicate) {
    return filter(keys(this.active), RequestHandler.triggerPredicate(this.active, predicate));
  }

  onSend(key, source, requestConfig, cb) {
    this.addActiveRequest(key, { requestConfig, source });
    isFunction(cb) && cb();
  }

  addActiveRequest(key, request) {
    this.active[key] = request;
    this.numActive++;
  }

  isAvailableSlot() {
    return this.getAvailableSlotsCount() > 0;
  }

  getAvailableSlotsCount() {
    return this.maxActive - this.numActive;
  }

  enqueueRequest(priority, requestConfig, onPending, onSuccess, onFailure, onCancel) {
    return this.priorityQueue.enqueue({
      priority,
      requestConfig,
      onPending,
      onSuccess,
      onFailure,
      onCancel,
    });
  }
}

export default RequestHandler;
